import React, { useState } from 'react';
import './CellHeatmap.css';

const CellHeatmap = ({ cells, temperature }) => {
  const [selectedCell, setSelectedCell] = useState(null);
  const [hoveredCell, setHoveredCell] = useState(null);

  if (!cells || !cells.voltages) return null;

  // Calculate color based on voltage
  const getHeatmapColor = (voltage) => {
    const min = cells.minVoltage || 3.60;
    const max = cells.maxVoltage || 3.75;
    const normalized = (voltage - min) / (max - min);

    // Create gradient from blue (low) to cyan (mid) to green (high)
    if (normalized < 0.33) {
      const t = normalized / 0.33;
      return `rgb(${Math.round(30 * t)}, ${Math.round(100 + 112 * t)}, ${Math.round(200 + 55 * t)})`;
    } else if (normalized < 0.67) {
      const t = (normalized - 0.33) / 0.34;
      return `rgb(${Math.round(30 + 170 * t)}, ${Math.round(212 - 27 * t)}, ${Math.round(255 - 124 * t)})`;
    } else {
      const t = (normalized - 0.67) / 0.33;
      return `rgb(${Math.round(200 - 184 * t)}, ${Math.round(185 + 4 * t)}, ${Math.round(131 - 2 * t)})`;
    }
  };

  // Get temperature color
  const getTempColor = (temp) => {
    if (temp < 25) return '#6366f1';
    if (temp < 30) return '#10b981';
    if (temp < 35) return '#f59e0b';
    return '#ef4444';
  };

  // Group cells into modules (assuming 14 modules of 9 cells each for 126 total)
  const modulesPerRow = 7;
  const cellsPerModule = 9;
  const modules = [];

  for (let i = 0; i < cells.count; i += cellsPerModule) {
    modules.push(cells.voltages.slice(i, i + cellsPerModule));
  }

  return (
    <div className="cell-heatmap-container">
      <div className="heatmap-header">
        <div className="heatmap-title">
          <span className="title-icon">🔋</span>
          <h4>Battery Cell Voltage Map ({cells.count} Cells)</h4>
        </div>
        <div className="heatmap-stats">
          <div className="stat-item">
            <span className="stat-label">Min</span>
            <span className="stat-value">{cells.minVoltage?.toFixed(3)}V</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Avg</span>
            <span className="stat-value">{cells.avgVoltage?.toFixed(3)}V</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Max</span>
            <span className="stat-value">{cells.maxVoltage?.toFixed(3)}V</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">ΔV</span>
            <span className="stat-value text-success">{cells.imbalance?.toFixed(3)}V</span>
          </div>
        </div>
      </div>

      {/* Cell Grid */}
      <div className="cell-modules-grid">
        {modules.map((module, moduleIdx) => (
          <div key={moduleIdx} className="cell-module">
            <div className="module-label">M{moduleIdx + 1}</div>
            <div className="module-cells">
              {module.map((voltage, cellIdx) => {
                const globalCellIdx = moduleIdx * cellsPerModule + cellIdx;
                const isSelected = selectedCell === globalCellIdx;
                const isHovered = hoveredCell === globalCellIdx;

                return (
                  <div
                    key={cellIdx}
                    className={`cell-item ${isSelected ? 'selected' : ''} ${isHovered ? 'hovered' : ''}`}
                    style={{
                      background: getHeatmapColor(voltage),
                      boxShadow: isSelected || isHovered
                        ? '0 0 0 2px var(--text)'
                        : 'none'
                    }}
                    onClick={() => setSelectedCell(isSelected ? null : globalCellIdx)}
                    onMouseEnter={() => setHoveredCell(globalCellIdx)}
                    onMouseLeave={() => setHoveredCell(null)}
                    title={`Cell ${globalCellIdx + 1}: ${voltage.toFixed(3)}V`}
                  >
                    {isSelected && <span className="cell-number">{globalCellIdx + 1}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="heatmap-legend">
        <span className="legend-label">Voltage:</span>
        <div className="legend-gradient">
          <div className="gradient-bar"></div>
          <div className="gradient-labels">
            <span>{cells.minVoltage?.toFixed(2)}V</span>
            <span>{((cells.minVoltage + cells.maxVoltage) / 2)?.toFixed(2)}V</span>
            <span>{cells.maxVoltage?.toFixed(2)}V</span>
          </div>
        </div>
      </div>

      {/* Selected Cell Detail */}
      {selectedCell !== null && (
        <div className="selected-cell-detail glass-panel fade-in">
          <div className="detail-header">
            <h5>Cell {selectedCell + 1} Details</h5>
            <button className="close-btn" onClick={() => setSelectedCell(null)}>×</button>
          </div>
          <div className="detail-content">
            <div className="detail-row">
              <span className="detail-label">Module</span>
              <span className="detail-value">M{Math.floor(selectedCell / cellsPerModule) + 1}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Position in Module</span>
              <span className="detail-value">Cell {(selectedCell % cellsPerModule) + 1}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Voltage</span>
              <span className="detail-value">{cells.voltages[selectedCell]?.toFixed(3)}V</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Temperature</span>
              <span className="detail-value" style={{ color: getTempColor(temperature?.cells?.[selectedCell] || 28) }}>
                {temperature?.cells?.[selectedCell]?.toFixed(1) || '28.0'}°C
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Deviation from Avg</span>
              <span className={`detail-value ${Math.abs(cells.voltages[selectedCell] - cells.avgVoltage) > 0.05 ? 'text-warning' : 'text-success'}`}>
                {((cells.voltages[selectedCell] - cells.avgVoltage) * 1000).toFixed(1)}mV
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Health Status</span>
              <span className="detail-value text-success">Normal</span>
            </div>
          </div>
        </div>
      )}

      {/* Hover Tooltip */}
      {hoveredCell !== null && selectedCell !== hoveredCell && (
        <div className="hover-tooltip">
          <div className="tooltip-content">
            <strong>Cell {hoveredCell + 1}</strong>
            <span>{cells.voltages[hoveredCell]?.toFixed(3)}V</span>
            <span>{temperature?.cells?.[hoveredCell]?.toFixed(1) || '28.0'}°C</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CellHeatmap;
